/**
 * @fileoverview UI-side helpers for report-parameter validation — thin adapters over
 *             the shared core-domain validator (single source of validation logic).
 * @module ui-admin/components/ParameterEditor/parameterValidators
 *
 * Responsibilities: - Provide per-parameter and per-binding validation wrappers for
 *                     the ParameterEditor / ParameterPromptModal / ParameterBindingSection
 *                     so they show field-level messages, not just a joined error string.
 * Dependencies:     - @firesquirrel/core-domain (validateReportParameters, types).
 * Architecture:     - Presentation adapters; ALL validation semantics live in
 *                     core-domain — this file only reshapes errors for form display.
 * Notes:            - Do NOT add validation logic here; add it to core-domain.
 */

import {
  validateReportParameters,
  type ReportParameter,
  type ParameterBinding,
  type ParameterMode,
} from '@firesquirrel/core-domain';

/**
 * @reusable parameterValidationMap
 * Purpose: Map validator output to a per-parameterId error map for inline display.
 * Rules:   Pure function; delegates to core-domain validateReportParameters.
 */
export function parameterValidationMap(parameters: ReportParameter[]): Map<string, string[]> {
  const errors = validateReportParameters(parameters);
  const map = new Map<string, string[]>();
  for (const err of errors) {
    const match = err.match(/'([^']+)'/);
    const key = match?.[1] ?? '*';
    map.set(key, [...(map.get(key) ?? []), err]);
  }
  return map;
}

/**
 * @reusable generateParameterId
 * Purpose: Suggest a unique parameterId when the user clicks "+ Add Parameter".
 * Rules:   Pure function; derives a slug from the label, suffixes when taken.
 */
export function generateParameterId(label: string, existing: ReportParameter[]): string {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, c: string) => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '') || 'param';
  const taken = new Set(existing.map((p) => p.parameterId));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}${i}`)) i += 1;
  return `${base}${i}`;
}

/**
 * @reusable isBindingValid
 * Purpose: Whether a single parameter binding is internally consistent
 *   (fixed → has a defined value; relative → has a token).
 * Rules:   Pure function; mirrors the binding rules validated at save time.
 */
export function isBindingValid(binding: ParameterBinding): boolean {
  if (binding.mode === 'relative') return !!binding.token;
  return binding.value !== undefined && binding.value !== null && binding.value !== '';
}

/**
 * @reusable defaultModeForType
 * Purpose: The sensible default ParameterMode when a parameter type changes
 *   (relative only makes sense for date/dateRange).
 * Rules:   Pure function.
 */
export function defaultModeForType(type: ReportParameter['type']): ParameterMode {
  return type === 'date' || type === 'dateRange' ? 'relative' : 'fixed';
}
